import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { tagsPropTypes, reduxFormPropTypes } from 'constants/propTypes';
import Tag from './Tag';
import './Tags.scss';

/*
  The intent is for Tags to be a Redux-Form/Field component.

  We operate in multiple of tagging contexts; all slightly different from each other.
  Some provide a tree of all possible tags and a list of the active tags IDs.
  Some provide a map with keys for all possible tags with values indicating active.
  Some include tags that we don't want the user to see. These can be active.

  To work for everyone, this component expects the following...

  input.value = {
    [tagId]: [active]
    // 'tag-1': true,
    // 'tag-2': false,
  }

    Notes:
      - keys with `false` values can be omitted
      - keys with `false` values will not be removed


  tagTree = [
    {
      id: 'tag-1',
      name: 'Tag One',
      subtags: [ {...} ],
    },
  ]

    Notes:
      - this is the list of tags to render and allow the user to edit
      - IDs present in `input.value` but not in `tagTree` will not be modified or rendered
      - tagTree will be parsed recursively


  isDisabled = (
    validIds, // ['aaa']
    activeTagIds, // { aaa: true }
    tag, // { id: 'bbb', ... name, subtags }
  ) => { ... return true|false }

    Notes:
      - this function is optional and, when not provided, tags will always be enabled


  If you need your submitted values to have a different shape, use the format/parse props on the Field.

  Example:
    - tags (from server, only includes active) = [ 'aaa', 'bbb', 'ccc' ]
    - allTags (from server, all possible tags) = [ 'aaa', 'bbb', 'ccc', 'ddd', 'eee' ]

    <Field
      component={Tags}
      // [a, b, c] --> { a:true, b:true, c:true }
      format={value => value.reduce((acc, x) => ({ ...acc, [x]: true }), {})}
      // { a:true, b:true, c:false } --> [a, b]
      parse={value => Object.entries(value).reduce((acc, [k,v]) => v ? [...acc, k] : acc, [])}
      // [a, b, c] --> [ { id:'a', name:'a', subtags:[] }, ... ]
      tagTree={allTags.map(id => ({ id, name: id, subtags: [] }))}
    />
*/

function getAllTagIds(tagTree) {
  let ids = [];
  tagTree.forEach(({ id, subtags = [] }) => {
    ids = ids.concat(id);
    if (subtags.length) {
      ids = ids.concat(getAllTagIds(subtags));
    }
  });
  return ids;
}

const Tags = ({ input, tagTree, isDisabled = () => false, className }) => {
  const { value: activeTagIds, onChange } = input;
  const validIds = getAllTagIds(tagTree);
  const handleChange = ({ id, subtags = [] }, event) => {
    const subtagUpdates = subtags.reduce(
      (acc, { id: sid }) => (activeTagIds[sid] ? { ...acc, [sid]: event.target.checked } : acc),
      {}
    );

    return onChange({
      ...activeTagIds,
      ...subtagUpdates,
      [id]: event.target.checked,
    });
  };

  return (
    <ul className={classNames('tags', className)}>
      {tagTree.map(tag => (
        <Tag
          key={tag.id}
          tag={tag}
          isDisabled={currentTag => isDisabled(validIds, activeTagIds, currentTag)}
          activeTagIds={activeTagIds}
          onChange={handleChange}
        />
      ))}
    </ul>
  );
};

Tags.propTypes = {
  isDisabled: PropTypes.func,
  tagTree: tagsPropTypes,
  ...reduxFormPropTypes.field,
};

export default Tags;
